import type { ReactNode } from 'react'
import { FileText, Zap } from 'lucide-react'
import { cn } from '../lib/cn'
import { Badge } from '../primitives/Badge'
import { SearchInput } from '../primitives/SearchInput'

export interface SlashItem {
  command: string
  description: string
  source?: 'local' | 'server'
}

export interface SlashMenuProps {
  query: string
  onQuery: (value: string) => void
  skills: SlashItem[]
  prompts: SlashItem[]
  onPick: (command: string) => void
  /** Hide the internal search box — used when the query is typed inline in the composer. */
  hideSearch?: boolean
  /** Command of the keyboard-highlighted row (inline mode). */
  activeCommand?: string
}

export function SlashMenu({
  query,
  onQuery,
  skills,
  prompts,
  onPick,
  hideSearch,
  activeCommand,
}: SlashMenuProps) {
  const empty = skills.length === 0 && prompts.length === 0
  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-lg">
      {!hideSearch && (
        <div className="p-2">
          <SearchInput
            value={query}
            onChange={onQuery}
            placeholder="Skills & Prompts durchsuchen…"
          />
        </div>
      )}
      <div className="max-h-72 overflow-y-auto pb-1">
        {skills.length > 0 && (
          <Section title="Skills">
            {skills.map((s) => (
              <Row
                key={s.command}
                active={activeCommand === s.command}
                onClick={() => onPick(s.command)}
              >
                <Zap className="h-4 w-4 shrink-0 text-brand-600" />
                <RowText command={s.command} description={s.description} />
              </Row>
            ))}
          </Section>
        )}
        {prompts.length > 0 && (
          <Section title="Prompts">
            {prompts.map((p) => (
              <Row
                key={p.command}
                active={activeCommand === p.command}
                onClick={() => onPick(p.command)}
              >
                <FileText className="h-4 w-4 shrink-0 text-accent" />
                <RowText command={p.command} description={p.description} />
                {p.source && (
                  <Badge tone={p.source === 'server' ? 'brand' : 'accent'}>
                    {p.source === 'server' ? 'Server' : 'Lokal'}
                  </Badge>
                )}
              </Row>
            ))}
          </Section>
        )}
        {empty && (
          <div className="px-3 py-6 text-center text-xs text-neutral-400">Keine Treffer</div>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="pt-1">
      <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
        {title}
      </div>
      {children}
    </div>
  )
}

function Row({
  children,
  onClick,
  active,
}: {
  children: ReactNode
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-neutral-50',
        active && 'bg-neutral-100',
      )}
    >
      {children}
    </button>
  )
}

function RowText({ command, description }: { command: string; description: string }) {
  return (
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-brand-700">{command}</span>
      <span className="block truncate text-xs text-neutral-500">{description}</span>
    </span>
  )
}
